import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  logout,
  selectIsLoading,
  selectUser,
  selectUserGroups,
  selectUserType,
} from '../store/slices/authSlice';
import {
  fetchUserScooters,
  selectScooters,
  selectScooterLoading,
} from '../store/slices/scooterSlice';
import {
  chipLabel,
  useScooterReadiness,
  type ScooterChipState,
} from '../hooks/useScooterReadiness';
import { r } from '../theme/riderTheme';
import {
  PageHeader,
  PageHeaderEyebrow,
  PageHeaderText,
  PageHeaderTitle,
  ButtonChevron,
  GhostButton,
} from '../theme/surfacePrimitives';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.section`
  position: relative;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 16px;
  padding: 18px;
  overflow: hidden;
`;

const IdentityCard = styled(Card)`
  padding: 22px 20px 20px;
`;

const IdentityLimeRail = styled.span`
  position: absolute;
  top: 16px;
  left: 0;
  bottom: 16px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: ${({ theme }) =>
    `linear-gradient(180deg, ${r(theme).accent.lime} 0%, ${r(theme).accent.limeDeep} 100%)`};
`;

const IdentityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-left: 8px;
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ theme }) => r(theme).cta.bg};
  color: ${({ theme }) => r(theme).accent.lime};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  box-shadow:
    0 0 0 1px ${({ theme }) => r(theme).cta.bg},
    0 0 0 4px ${({ theme }) => r(theme).surface.sheet},
    0 0 0 5px ${({ theme }) => r(theme).accent.lime}50;
`;

const IdentityText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DisplayName = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${({ theme }) => r(theme).text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmailRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => r(theme).text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VerifiedDot = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme, $verified }) =>
    $verified ? r(theme).accent.ok : r(theme).accent.alert};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 6px currentColor;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
  padding-left: 8px;
`;

const MetaChip = styled.span<{ $tone?: 'lime' | 'neutral' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;

  ${({ $tone, theme }) => {
    const t = r(theme);
    switch ($tone) {
      case 'lime':
        return css`
          background: ${t.surface.raisedSelected};
          border: 1px solid ${t.accent.limeDeep};
          color: ${t.accent.ok};
        `;
      case 'info':
        return css`
          background: rgba(0, 215, 255, 0.06);
          border: 1px solid rgba(0, 215, 255, 0.32);
          color: #0884a0;
        `;
      case 'neutral':
      default:
        return css`
          background: ${t.surface.inputIdle};
          border: 1px solid ${t.surface.edge};
          color: ${t.text.muted};
        `;
    }
  }}
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 0 2px 10px;
`;

const SectionEyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const SectionCount = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
`;

const ScooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const ScooterRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 4px;
  border-top: 1px solid ${({ theme }) => r(theme).surface.edge};

  &:first-child {
    border-top: 0;
  }
`;

const ScooterIdent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ScooterPlate = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
`;

const ScooterModel = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0;
  color: ${({ theme }) => r(theme).text.muted};
`;

const ScooterVin = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: ${({ theme }) => r(theme).text.faint};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScooterChip = styled.span<{ $state: ScooterChipState }>`
  flex-shrink: 0;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: 999px;

  ${({ $state, theme }) => {
    const t = r(theme);
    switch ($state) {
      case 'restricted':
      case 'needs_packs':
        return css`
          background: rgba(255, 25, 35, 0.05);
          border: 1px solid rgba(255, 25, 35, 0.32);
          color: ${t.accent.alert};
        `;
      case 'ready':
        return css`
          background: ${t.surface.raisedSelected};
          border: 1px solid ${t.accent.limeDeep};
          color: ${t.accent.ok};
        `;
      case 'unavailable':
      case 'checking':
      default:
        return css`
          background: ${t.surface.inputIdle};
          border: 1px solid ${t.surface.edge};
          color: ${t.text.faint};
        `;
    }
  }}
`;

const SignOutCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SignOutCopy = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => r(theme).text.muted};
`;

const SignOutAction = styled(GhostButton)`
  border-color: ${({ theme }) => r(theme).accent.alert};
  color: ${({ theme }) => r(theme).accent.alert};

  &:not(:disabled):hover {
    background: rgba(255, 25, 35, 0.05);
    border-color: ${({ theme }) => r(theme).accent.alert};
  }
`;

const getDisplayName = (
  fullName: string | undefined,
  givenName: string | undefined,
  email: string,
): string => {
  if (fullName && fullName.trim()) return fullName.trim();
  if (givenName && givenName.trim()) return givenName.trim();
  return email.split('@')[0];
};

const getInitials = (name: string): string =>
  name
    .split(/[\s.\-_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || '?';

const formatUserType = (type: string | undefined): { label: string; tone: 'lime' | 'info' | 'neutral' } => {
  switch (type) {
    case 'fleet':
      return { label: 'Fleet Rider', tone: 'info' };
    case 'individual':
      return { label: 'Individual Rider', tone: 'lime' };
    case 'staff':
      return { label: 'Staff', tone: 'neutral' };
    case 'admin':
      return { label: 'Admin', tone: 'neutral' };
    default:
      return { label: type ?? 'Rider', tone: 'neutral' };
  }
};

const AccountPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const userType = useAppSelector(selectUserType);
  const userGroups = useAppSelector(selectUserGroups);
  const isLoading = useAppSelector(selectIsLoading);
  const scooters = useAppSelector(selectScooters);
  const scooterLoading = useAppSelector(selectScooterLoading);
  const readinessByVin = useScooterReadiness(scooters);

  useEffect(() => {
    if (scooters.length === 0 && !scooterLoading.scooters) {
      void dispatch(fetchUserScooters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  if (!user) {
    return null;
  }

  const displayName = getDisplayName(
    user['custom:full_name'],
    user.given_name,
    user.email,
  );
  const initials = getInitials(displayName);
  const typeInfo = formatUserType(userType);

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  return (
    <Container>
      <PageHeader>
        <PageHeaderText>
          <PageHeaderEyebrow>Account · Profile</PageHeaderEyebrow>
          <PageHeaderTitle>Profile</PageHeaderTitle>
        </PageHeaderText>
      </PageHeader>

      <IdentityCard>
        <IdentityLimeRail />
        <IdentityHeader>
          <Avatar aria-hidden>{initials}</Avatar>
          <IdentityText>
            <DisplayName>{displayName}</DisplayName>
            <EmailRow title={user.email}>
              <span>{user.email}</span>
              <VerifiedDot $verified={user.email_verified}>
                {user.email_verified ? 'Verified' : 'Unverified'}
              </VerifiedDot>
            </EmailRow>
          </IdentityText>
        </IdentityHeader>

        <MetaRow>
          <MetaChip $tone={typeInfo.tone}>{typeInfo.label}</MetaChip>
          {userGroups.map(group => (
            <MetaChip key={group} $tone="neutral">
              {group}
            </MetaChip>
          ))}
        </MetaRow>
      </IdentityCard>

      {scooters.length > 0 && (
        <Card>
          <SectionHeader>
            <SectionEyebrow>Scooters · Assigned</SectionEyebrow>
            <SectionCount>
              {scooters.length} {scooters.length === 1 ? 'Scooter' : 'Scooters'}
            </SectionCount>
          </SectionHeader>
          <ScooterList>
            {scooters.map(scooter => {
              const chipState: ScooterChipState =
                readinessByVin[scooter.vin] ?? 'checking';
              const plate = scooter.plate_number?.trim() || scooter.vin.slice(-6);
              const model = scooter.model?.trim();
              return (
                <ScooterRow key={scooter.vin}>
                  <ScooterIdent>
                    <ScooterPlate>
                      {plate}
                      {model ? <ScooterModel>{model}</ScooterModel> : null}
                    </ScooterPlate>
                    <ScooterVin>VIN · {scooter.vin}</ScooterVin>
                  </ScooterIdent>
                  <ScooterChip $state={chipState}>
                    {chipLabel(chipState)}
                  </ScooterChip>
                </ScooterRow>
              );
            })}
          </ScooterList>
        </Card>
      )}

      <SignOutCard>
        <SectionEyebrow>Session</SectionEyebrow>
        <SignOutCopy>
          Sign out of this device. Your assigned scooters and active packs are unaffected.
        </SignOutCopy>
        <SignOutAction
          onClick={handleSignOut}
          $disabled={isLoading}
          disabled={isLoading}
          aria-label="Sign out of this device"
        >
          <span>{isLoading ? 'Signing out…' : 'Sign out'}</span>
          <ButtonChevron aria-hidden>→</ButtonChevron>
        </SignOutAction>
      </SignOutCard>
    </Container>
  );
};

export default AccountPage;
